import { forwardRef, type HTMLAttributes, type ReactNode } from 'react'
import { cn } from '@lib/utils'

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  /** Card visual variant */
  variant?: 'default' | 'elevated' | 'outlined' | 'interactive'
  /** Remove default padding */
  noPadding?: boolean
}

/**
 * Card container component with brutalist styling
 *
 * @example
 * <Card>Basic card content</Card>
 * <Card variant="elevated">Elevated card</Card>
 * <Card variant="interactive" onClick={handleClick}>Clickable card</Card>
 */
export const Card = forwardRef<HTMLDivElement, CardProps>(
  ({ className, variant = 'default', noPadding = false, children, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(
          // Base styles
          'bg-surface transition-all',

          // Padding
          !noPadding && 'p-6',

          // Variant styles
          variant === 'default' && 'shadow-brutalist',

          variant === 'elevated' && 'shadow-brutalist-lg',

          variant === 'outlined' && 'border border-border shadow-none',

          variant === 'interactive' && [
            'shadow-brutalist cursor-pointer',
            'hover:shadow-brutalist-hover hover:-translate-x-[2px] hover:-translate-y-[2px]',
            'active:shadow-brutalist-pressed active:translate-x-[2px] active:translate-y-[2px]',
          ],

          className
        )}
        {...props}
      >
        {children}
      </div>
    )
  }
)

Card.displayName = 'Card'

/**
 * Card header section
 */
export interface CardHeaderProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  /** Card title */
  title?: ReactNode
  /** Card description/subtitle */
  description?: ReactNode
  /** Actions to show on right side */
  action?: ReactNode
}

export type CardContentProps = HTMLAttributes<HTMLDivElement>
export type CardFooterProps = HTMLAttributes<HTMLDivElement>

export const CardHeader = forwardRef<HTMLDivElement, CardHeaderProps>(
  ({ className, title, description, action, children, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn('flex items-start justify-between gap-4', className)}
        {...props}
      >
        <div className="flex-1 space-y-1">
          {title && (
            <h3 className="text-h4 font-semibold text-text-primary">{title}</h3>
          )}
          {description && (
            <p className="text-body-sm text-text-secondary">{description}</p>
          )}
          {children}
        </div>
        {action && <div className="shrink-0">{action}</div>}
      </div>
    )
  }
)

CardHeader.displayName = 'CardHeader'

/**
 * Card content section
 */
export const CardContent = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => {
    return <div ref={ref} className={cn('mt-4', className)} {...props} />
  }
)

CardContent.displayName = 'CardContent'

/**
 * Card footer section
 */
export const CardFooter = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn('mt-6 flex items-center gap-4 border-t border-border pt-4', className)}
        {...props}
      />
    )
  }
)

CardFooter.displayName = 'CardFooter'

/**
 * Stat card for displaying metrics
 */
export interface StatCardProps extends Omit<CardProps, 'children'> {
  /** Metric label */
  label: string
  /** Metric value */
  value: string | number
  /** Trend indicator */
  trend?: {
    value: number
    direction: 'up' | 'down' | 'neutral'
  }
  /** Icon to display */
  icon?: ReactNode
  /** Optional description */
  description?: string
}

export const StatCard = forwardRef<HTMLDivElement, StatCardProps>(
  ({ className, label, value, trend, icon, description, ...props }, ref) => {
    return (
      <Card ref={ref} className={cn('', className)} {...props}>
        <div className="flex items-start justify-between">
          <div className="space-y-2">
            <p className="text-body-sm text-text-secondary">{label}</p>
            <p className="text-h2 font-bold text-text-primary tabular-nums">{value}</p>
            {trend && (
              <div
                className={cn(
                  'inline-flex items-center gap-1 text-body-sm font-medium',
                  trend.direction === 'up' && 'text-success',
                  trend.direction === 'down' && 'text-error',
                  trend.direction === 'neutral' && 'text-text-muted'
                )}
              >
                {trend.direction === 'up' && <TrendUpIcon />}
                {trend.direction === 'down' && <TrendDownIcon />}
                {trend.direction === 'neutral' && <TrendNeutralIcon />}
                <span>{trend.value > 0 ? '+' : ''}{trend.value}%</span>
              </div>
            )}
            {description && (
              <p className="text-caption text-text-muted">{description}</p>
            )}
          </div>
          {icon && (
            <div className="text-primary opacity-80">{icon}</div>
          )}
        </div>
      </Card>
    )
  }
)

StatCard.displayName = 'StatCard'

// Trend icons
function TrendUpIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
      <polyline points="17 6 23 6 23 12" />
    </svg>
  )
}

function TrendDownIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polyline points="23 18 13.5 8.5 8.5 13.5 1 6" />
      <polyline points="17 18 23 18 23 12" />
    </svg>
  )
}

function TrendNeutralIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  )
}

export default Card
